import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Divider,
  InputAdornment,
  FormHelperText,
  Switch,
  FormControlLabel,
  Avatar,
  Badge,
  useTheme,
  alpha,
  Skeleton,
  styled,
  Fade,
  Zoom,
  Collapse,
  Stepper,
  Step,
  StepLabel,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  School as SchoolIcon,
  Forum as ForumIcon,
  People as PeopleIcon,
  Create as ContentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassFull as PendingIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Payment as TokenIcon,
  Star as StarIcon,
  Category as CategoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Timer as TimerIcon,
  Done as DoneIcon,
  Pending as InReviewIcon,
  Assignment as AssignmentIcon,
  History as HistoryIcon,
  MoreVert as MoreVertIcon,
  Info as InfoIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Description as DocumentIcon
} from '@mui/icons-material';
import { tenantTokenService, tokenEarningService } from '../../../services/skillcoinService';
import memberService from '../../../services/memberService';
import { formatDistanceToNow } from 'date-fns';

const SUBMISSION_STATUS = {
  SUBMITTED: 'submitted',
  IN_REVIEW: 'in_review',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

// Styled components for modern UI
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
  },
}));

const TokenChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, #3a7bd5, #00d2ff)',
  color: '#fff',
  fontWeight: 'bold',
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 20px',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '16px',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: alpha(theme.palette.primary.main, 0.05),
  '& .MuiTableCell-root': {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
}));

// Activity Status Indicator Component
const ActivityStatusIndicator = ({ isActive }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        variant="dot"
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: isActive ? '#44b700' : '#bdbdbd',
            boxShadow: `0 0 0 2px white`,
          },
        }}
      >
        <Chip 
          label={isActive ? 'Active' : 'Inactive'} 
          color={isActive ? 'success' : 'default'} 
          size="small"
          sx={{
            minWidth: 80, 
            fontWeight: 500,
            backgroundColor: isActive ? alpha('#44b700', 0.1) : alpha('#bdbdbd', 0.1)
          }}
        />
      </Badge>
    </Box>
  );
};

const ActivityTypeIcon = ({ type }) => {
  const iconMap = {
    'content_creation': <EditIcon />,
    'knowledge_sharing': <SchoolIcon />,
    'task_completion': <AssignmentIcon />,
    'community_support': <PeopleIcon />,
    'skill_validation': <StarIcon />,
    'event_participation': <EventIcon />,
    'referral': <PeopleIcon />,
    'custom': <CategoryIcon />,
    'forum_activity': <ForumIcon /> // Keep this for backward compatibility
  };
  return iconMap[type] || <CategoryIcon />;
};

const MaintenanceMessage = () => (
  <Paper sx={{ p: 3, textAlign: 'center' }}>
    <Typography variant="body1" color="textSecondary">
      This section is currently empty. Please check back later.
    </Typography>
  </Paper>
);

// Add this function to the component to render file attachments
const renderFileAttachments = (files) => {
  if (!files || files.length === 0) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" gutterBottom>File Attachments ({files.length})</Typography>
      <Grid container spacing={1}>
        {files.map((file, index) => {
          const isImage = file.contentType && file.contentType.startsWith('image/');
          const isPDF = file.contentType === 'application/pdf';
          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 1, 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                {isImage ? (
                  <Box sx={{ mb: 1, width: '100%', height: 140, position: 'relative' }}>
                    <Box
                      component="img"
                      src={file.url}
                      alt={file.filename}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                ) : isPDF ? (
                  <PdfIcon color="error" sx={{ fontSize: 60, mb: 1 }} />
                ) : (
                  <DocumentIcon color="primary" sx={{ fontSize: 60, mb: 1 }} />
                )}
                <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
                  {file.filename}
                </Typography>
                <Button 
                  size="small"
                  component="a"
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mt: 1 }}
                  startIcon={isImage ? <ImageIcon /> : isPDF ? <PdfIcon /> : <FileIcon />}
                >
                  View
                </Button>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

// Main component
const EarningActivities = () => {
  const theme = useTheme();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [currentActivity, setCurrentActivity] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [view, setView] = useState('grid');
  const [currentTab, setCurrentTab] = useState(0);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [currentSubmission, setCurrentSubmission] = useState(null);
  const [adminFeedback, setAdminFeedback] = useState('');
  const [submissionAction, setSubmissionAction] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [memberCache, setMemberCache] = useState({});
  
  // Form state for controlled inputs
  const [formData, setFormData] = useState({
    name: '',
    activityType: 'content_creation',
    description: '',
    tokenAmount: 10,
    frequency: 'once',
    maxDaily: 0,
    isActive: true,
  });

  useEffect(() => {
    fetchEarningActivities();
    fetchActivitySubmissions();
  }, []);

  // Add this effect to properly handle form initialization when editing
  useEffect(() => {
    // When the form opens, initialize or reset the form state
    if (formOpen) {
      if (currentActivity) {
        // If editing an existing activity, populate the form with its data
        console.log('Initializing form with activity data:', currentActivity);
        setFormData({
          name: currentActivity.name || '',
          activityType: currentActivity.activityType || 'content_creation',
          description: currentActivity.description || '',
          tokenAmount: currentActivity.tokenAmount || 10,
          frequency: currentActivity.frequency || 'once',
          maxDaily: currentActivity.maxDaily || 0,
          isActive: currentActivity.isActive !== undefined ? currentActivity.isActive : true,
        });
      } else {
        // If creating a new activity, reset to defaults
        setFormData({
          name: '',
          activityType: 'content_creation',
          description: '',
          tokenAmount: 10,
          frequency: 'once',
          maxDaily: 0,
          isActive: true,
        });
      }
    }
  }, [formOpen, currentActivity]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const fetchEarningActivities = async () => {
    setLoading(true);
    try {
      console.log('Fetching earning activities in component');
      // Use the tokenEarningService instead of tenantTokenService for better fallback handling
      const data = await tokenEarningService.getEarningActivities();
      console.log('Fetched activities:', data);
      setActivities(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching earning activities:', err);
      setError('Failed to load earning activities');
      // Set empty array to avoid UI breakage
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivitySubmissions = async () => {
    setLoadingSubmissions(true);
    try {
      console.log('Fetching activity submissions from API');
      
      // Get tenant ID from localStorage
      const tenantId = localStorage.getItem('tenantId');
      if (!tenantId) {
        throw new Error('Tenant ID not found in local storage');
      }
      
      // Get auth token from local storage
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!authToken) {
        throw new Error('Authentication token not found');
      }
      
      // Call the API to get real submissions
      const response = await fetch(`${process.env.VITE_API_URL || 'http://localhost:5000/api'}/v1/skillcoin/activities/admin/submissions?tenantId=${tenantId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'x-tenant-id': tenantId,
        }
      });
      
      // If there's an API error, throw it to be caught by the catch block
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Activity submissions response:', data);
      
      if (data.success && Array.isArray(data.data)) {
        // Format the data to match the component's expected structure
        const formattedSubmissions = data.data.map(submission => {
          // Improved user name handling with multiple fallbacks
          let memberName = 'Unknown User';
          
          // Check for userName field first (direct value)
          if (submission.userName && typeof submission.userName === 'string') {
            memberName = submission.userName;
          } 
          // Check if userId exists and has name properties
          else if (submission.userId) {
            // Handle if userId is an object with firstName/lastName
            if (submission.userId.firstName || submission.userId.lastName) {
              const firstName = submission.userId.firstName || '';
              const lastName = submission.userId.lastName || '';
              memberName = `${firstName} ${lastName}`.trim();
            } 
            // Handle if userId has name property
            else if (submission.userId.name) {
              memberName = submission.userId.name;
            }
            // Handle if userId has displayName property
            else if (submission.userId.displayName) {
              memberName = submission.userId.displayName;
            }
          }

          return {
            _id: submission._id,
            activityId: submission.activityId?._id || submission.activityId,
            activityName: submission.activityName || 'Unknown Activity',
            activityType: submission.activityType || 'custom',
            tokenAmount: submission.tokenAmount || 0,
            memberId: submission.userId?._id || submission.userId,
            memberName: memberName,
            memberEmail: submission.userEmail || 
              (submission.userId?.email) || '',
            evidence: submission.evidence || '',
            notes: submission.notes || '',
            status: (submission.status || 'submitted').toLowerCase(),
            files: submission.files || [],
            adminFeedback: submission.adminFeedback || '',
            submittedAt: new Date(submission.submittedAt || Date.now()),
            reviewStartedAt: submission.reviewStartedAt ? new Date(submission.reviewStartedAt) : null,
            reviewedAt: submission.reviewedAt ? new Date(submission.reviewedAt) : null,
            reviewedBy: submission.reviewedBy || null,
            transactionId: submission.transactionId || null
          };
        });
        setSubmissions(formattedSubmissions);
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (err) {
      console.error('Error fetching activity submissions:', err);
      
      // Show error message to the user
      setSnackbar({
        open: true,
        message: `Failed to fetch activity submissions: ${err.message}. Please try again.`,
        severity: 'error'
      });
      
      // Set empty submissions array to avoid UI breakage
      setSubmissions([]);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const handleOpenAddForm = () => {
    setCurrentActivity(null);
    setFormError(null);
    setFormOpen(true);
  };

  const handleOpenEditForm = (activity) => {
    setCurrentActivity(activity);
    setFormError(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
  };

  const handleOpenDeleteConfirm = (activity) => {
    setActivityToDelete(activity);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
  };

  const handleDeleteActivity = async () => {
    if (!activityToDelete) return;
    try {
      await tokenEarningService.deleteEarningActivity(activityToDelete._id);
      setSnackbar({
        open: true,
        message: 'Activity deleted successfully',
        severity: 'success'
      });
      fetchEarningActivities(); // Refresh the list
    } catch (err) {
      console.error('Error deleting activity:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete activity',
        severity: 'error'
      });
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getActivityTypeLabel = (type) => {
    const types = {
      'content_creation': 'Content Creation',
      'forum_activity': 'Forum Activity',
      'event_participation': 'Event Participation',
      'knowledge_sharing': 'Knowledge Sharing',
      'course_completion': 'Course Completion',
      'community_contribution': 'Community Contribution',
      'shopping': 'Shopping',
      'other': 'Other'
    };
    return types[type] || type;
  };

  const filteredActivities = activities
    .filter(activity => {
      // Apply search filter
      if (searchTerm && !activity.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !activity.description.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      // Apply type filter
      if (filterType !== 'all' && activity.activityType !== filterType) {
        return false;
      }
      
      return true;
    });

  const submissionCounts = {
    submitted: submissions.filter(s => s.status === SUBMISSION_STATUS.SUBMITTED).length,
    inReview: submissions.filter(s => s.status === SUBMISSION_STATUS.IN_REVIEW).length,
    approved: submissions.filter(s => s.status === SUBMISSION_STATUS.APPROVED).length,
    rejected: submissions.filter(s => s.status === SUBMISSION_STATUS.REJECTED).length,
  };

  const handleOpenReviewDialog = (submission) => {
    setCurrentSubmission(submission);
    setAdminFeedback('');
    setSubmissionAction('');
    setReviewDialogOpen(true);
  };

  const handleCloseReviewDialog = () => {
    setReviewDialogOpen(false);
    setCurrentSubmission(null);
    setAdminFeedback('');
    setSubmissionAction('');
  };

  const handleUpdateSubmissionStatus = async (action) => {
    if (!currentSubmission) return;
    
    setActionLoading(true);
    setSubmissionAction(action);
    try {
      console.log(`${action === 'approve' ? 'Approving' : 'Rejecting'} submission:`, currentSubmission._id);
      
      // Prepare the request data
      const requestData = {
        status: action === 'approve' ? SUBMISSION_STATUS.APPROVED : SUBMISSION_STATUS.REJECTED,
        adminFeedback: adminFeedback,
        reviewerId: localStorage.getItem('userId') || 'current-admin'
      };
      
      // Call the API with the corrected endpoint
      const response = await fetch(`${process.env.VITE_API_URL || 'http://localhost:5000/api'}/v1/skillcoin/activities/admin/submissions/${currentSubmission._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem('tenantId'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      // Check if the API request was successful
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Server error: ${response.status}`);
      }
      
      // Process the response
      const responseData = await response.json();
      console.log('Submission status update response:', responseData);
      
      // Show detailed success message if tokens were awarded
      if (action === 'approve' && responseData.data && responseData.data.wallet) {
        const { wallet } = responseData.data;
        setSnackbar({
          open: true,
          message: `Submission approved and ${currentSubmission.tokenAmount} tokens credited to member's wallet (New balance: ${wallet.balance})`,
          severity: 'success'
        });
      } else {
        // Standard success message
        setSnackbar({
          open: true,
          message: `Submission ${action === 'approve' ? 'approved' : 'rejected'} successfully`,
          severity: 'success'
        });
      }
      
      // Update the local state
      fetchActivitySubmissions();
      
      // Close the dialog
      handleCloseReviewDialog();
    } catch (err) {
      console.error(`Error ${action === 'approve' ? 'approving' : 'rejecting'} submission:`, err);
      setSnackbar({
        open: true,
        message: `Failed to ${action} submission: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
      setSubmissionAction('');
    }
  };

  const handleMarkAsInReview = async (submissionId) => {
    try {
      console.log('Marking submission as in review:', submissionId);
      
      // Prepare the request data
      const requestData = {
        submissionId: submissionId,
        status: SUBMISSION_STATUS.IN_REVIEW,
        reviewerId: localStorage.getItem('userId') || 'current-admin'
      };
      
      // Call the API to update the submission status
      const response = await fetch(`${process.env.VITE_API_URL || 'http://localhost:5000/api'}/v1/skillcoin/activities/admin/submissions/${submissionId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem('tenantId'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      // If the endpoint isn't available or there's an error, we'll still update the UI
      // But we'll note it in the console
      if (!response.ok) {
        console.warn('API endpoint for marking as in-review not available, updating UI only');
      } else {
        console.log('Submission marked as in-review via API');
      }
      
      // Update the local state to reflect the change
      const updatedSubmissions = submissions.map(sub => {
        if (sub._id === submissionId) {
          return {
            ...sub,
            status: SUBMISSION_STATUS.IN_REVIEW,
            reviewStartedAt: new Date(),
            reviewedBy: requestData.reviewerId
          };
        }
        return sub;
      });
      
      setSubmissions(updatedSubmissions);
      setSnackbar({
        open: true,
        message: 'Submission marked as in review',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error marking submission as in review:', err);
      
      // Even if the API call fails, we'll update the UI for a better user experience
      const updatedSubmissions = submissions.map(sub => {
        if (sub._id === submissionId) {
          return {
            ...sub,
            status: SUBMISSION_STATUS.IN_REVIEW,
            reviewStartedAt: new Date(),
            reviewedBy: localStorage.getItem('userId') || 'current-admin'
          };
        }
        return sub;
      });
      
      setSubmissions(updatedSubmissions);
      setSnackbar({
        open: true,
        message: 'Marked as in review (offline mode)',
        severity: 'warning'
      });
    }
  };

  const handleToggleRow = (submissionId) => {
    if (expandedRow === submissionId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(submissionId);
    }
  };

  const handleSaveActivity = async (e) => {
    e?.preventDefault();
    try {
      setFormLoading(true);
      setFormError(null);
      
      console.log('About to submit activity data:', formData);
      
      if (!formData.name) {
        throw new Error('Activity name is required');
      }
      
      if (formData.tokenAmount <= 0) {
        throw new Error('Token amount must be greater than zero');
      }
      
      if (currentActivity) {
        // Update existing activity
        await tokenEarningService.updateEarningActivity(currentActivity._id, formData);
        setSnackbar({
          open: true,
          message: 'Activity updated successfully',
          severity: 'success'
        });
      } else {
        // Create new activity
        await tokenEarningService.createEarningActivity(formData);
        setSnackbar({
          open: true,
          message: 'Activity created successfully',
          severity: 'success'
        });
      }
      
      // Wait a moment before refreshing to allow server to process
      setTimeout(() => {
        fetchEarningActivities();
      }, 1000);
      
      // Close the form dialog
      handleCloseForm();
    } catch (err) {
      console.error('Error saving activity:', err);
      setFormError(`Failed to save activity: ${err.message || 'Unknown error'}`);
    } finally {
      setFormLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case SUBMISSION_STATUS.SUBMITTED:
        return theme.palette.warning.main;
      case SUBMISSION_STATUS.IN_REVIEW:
        return theme.palette.info.main;
      case SUBMISSION_STATUS.APPROVED:
        return theme.palette.success.main;
      case SUBMISSION_STATUS.REJECTED:
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case SUBMISSION_STATUS.SUBMITTED:
        return <PendingIcon />;
      case SUBMISSION_STATUS.IN_REVIEW:
        return <InReviewIcon />;
      case SUBMISSION_STATUS.APPROVED:
        return <CheckCircleIcon />;
      case SUBMISSION_STATUS.REJECTED:
        return <CancelIcon />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case SUBMISSION_STATUS.SUBMITTED:
        return 'Submitted';
      case SUBMISSION_STATUS.IN_REVIEW:
        return 'In Review';
      case SUBMISSION_STATUS.APPROVED:
        return 'Approved';
      case SUBMISSION_STATUS.REJECTED:
        return 'Rejected';
      default:
        return 'Unknown';
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
        <Tab label="Earning Activities" icon={<CategoryIcon />} iconPosition="start" />
        <Tab label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Badge badgeContent={submissionCounts.submitted + submissionCounts.inReview} color="primary" max={99}>
              <AssignmentIcon />
            </Badge>
            <Box sx={{ ml: 1 }}>Submissions</Box>
          </Box>
        } />
      </Tabs>

      {currentTab === 0 && (
        <Box sx={{ mt: 3 }}>
          {/* Search and filter controls */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ flexGrow: 1, maxWidth: { xs: '100%', sm: 300 } }}
            />
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="All Types"
                onClick={() => setFilterType('all')}
                color={filterType === 'all' ? 'primary' : 'default'}
                variant={filterType === 'all' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Content"
                icon={<ContentIcon />}
                onClick={() => setFilterType('content_creation')}
                color={filterType === 'content_creation' ? 'primary' : 'default'}
                variant={filterType === 'content_creation' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Forum"
                icon={<ForumIcon />}
                onClick={() => setFilterType('forum_activity')}
                color={filterType === 'forum_activity' ? 'primary' : 'default'}
                variant={filterType === 'forum_activity' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Events"
                icon={<EventIcon />}
                onClick={() => setFilterType('event_participation')}
                color={filterType === 'event_participation' ? 'primary' : 'default'}
                variant={filterType === 'event_participation' ? 'filled' : 'outlined'}
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddForm}
            >
              New Activity
            </Button>
          </Box>
          
          {/* Activities in grid view */}
          <Grid container spacing={3}>
            {filteredActivities.length === 0 ? (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="textSecondary">No activities match your search criteria</Typography>
                </Paper>
              </Grid>
            ) : (
              filteredActivities.map((activity) => (
                <Grid item xs={12} sm={6} md={4} key={activity._id}>
                  <StyledCard>
                    <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar
                            sx={{
                              bgcolor: theme.palette.primary.main,
                              color: theme.palette.primary.contrastText,
                              mr: 1,
                            }}
                          >
                            <ActivityTypeIcon type={activity.activityType} />
                          </Avatar>
                          <Typography variant="h6" noWrap>
                            {activity.name}
                          </Typography>
                        </Box>
                        <ActivityStatusIndicator isActive={activity.isActive} />
                      </Box>
                    </Box>
                    <CardContent sx={{ pt: 2 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {activity.description}
                      </Typography>
                      
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Reward
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            <TokenChip
                              icon={<TokenIcon />}
                              label={`${activity.tokenAmount} Tokens`}
                              size="small"
                            />
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Type
                          </Typography>
                          <Typography variant="body2">
                            {getActivityTypeLabel(activity.activityType)}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Frequency
                          </Typography>
                          <Typography variant="body2">
                            {activity.frequency || 'Once'}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Daily Limit
                          </Typography>
                          <Typography variant="body2">
                            {activity.maxDaily > 0 ? `${activity.maxDaily} per day` : 'No limit'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                    
                    <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                      <ActionButton
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEditForm(activity)}
                      >
                        Edit
                      </ActionButton>
                      <ActionButton
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleOpenDeleteConfirm(activity)}
                      >
                        Delete
                      </ActionButton>
                    </CardActions>
                  </StyledCard>
                </Grid>
              ))
            )}
          </Grid>
        </Box>
      )}

      {/* Submissions Tab */}
      {currentTab === 1 && (
        <Box sx={{ mt: 3 }}>
          {/* Stats Overview */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: theme.palette.warning.main, mr: 1 }}>
                    <PendingIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5">{submissionCounts.submitted}</Typography>
                    <Typography variant="body2" color="textSecondary">Submitted</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: theme.palette.info.main, mr: 1 }}>
                    <InReviewIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5">{submissionCounts.inReview}</Typography>
                    <Typography variant="body2" color="textSecondary">In Review</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: theme.palette.success.main, mr: 1 }}>
                    <CheckCircleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5">{submissionCounts.approved}</Typography>
                    <Typography variant="body2" color="textSecondary">Approved</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: theme.palette.error.main, mr: 1 }}>
                    <CancelIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5">{submissionCounts.rejected}</Typography>
                    <Typography variant="body2" color="textSecondary">Rejected</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Submissions Table */}
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            {loadingSubmissions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : submissions.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">No submissions found</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <StyledTableHead>
                    <TableRow>
                      <StyledTableCell>Activity</StyledTableCell>
                      <StyledTableCell>Member</StyledTableCell>
                      <StyledTableCell>Reward</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                      <StyledTableCell>Submitted</StyledTableCell>
                      <StyledTableCell>Actions</StyledTableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    {submissions.map((submission) => (
                      <React.Fragment key={submission._id}>
                        <TableRow 
                          hover
                          onClick={() => handleToggleRow(submission._id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.7), mr: 1 }}>
                                <ActivityTypeIcon type={submission.activityType} />
                              </Avatar>
                              <Typography variant="body2">{submission.activityName}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">
                                {memberCache[submission.memberId]?.name || submission.memberName}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {memberCache[submission.memberId]?.email || submission.memberEmail}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<TokenIcon />}
                              label={`${submission.tokenAmount} Tokens`}
                              size="small"
                              color="primary"
                              variant={submission.status === SUBMISSION_STATUS.APPROVED ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(submission.status)}
                              label={getStatusLabel(submission.status)}
                              size="small"
                              color={
                                submission.status === SUBMISSION_STATUS.APPROVED ? 'success' :
                                submission.status === SUBMISSION_STATUS.REJECTED ? 'error' :
                                submission.status === SUBMISSION_STATUS.IN_REVIEW ? 'info' : 'warning'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {formatDistanceToNow(new Date(submission.submittedAt), { addSuffix: true })}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex' }}>
                              {submission.status === SUBMISSION_STATUS.SUBMITTED && (
                                <Tooltip title="Mark as In Review">
                                  <IconButton 
                                    size="small"
                                    color="info"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMarkAsInReview(submission._id);
                                    }}
                                  >
                                    <InReviewIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              
                              {(submission.status === SUBMISSION_STATUS.SUBMITTED || 
                                submission.status === SUBMISSION_STATUS.IN_REVIEW) && (
                                <Tooltip title="Review">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenReviewDialog(submission);
                                    }}
                                  >
                                    <SearchIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              
                              <IconButton 
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleRow(submission._id);
                                }}
                              >
                                {expandedRow === submission._id ? 
                                  <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expanded Details Row */}
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                            <Collapse in={expandedRow === submission._id} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 3, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                                <Grid container spacing={3}>
                                  {/* Left Column - Submission Details */}
                                  <Grid item xs={12} md={8}>
                                    <Typography variant="subtitle1" gutterBottom>Submission Evidence</Typography>
                                    <Paper sx={{ p: 2, mb: 2 }}>
                                      <Typography variant="body2">{submission.evidence}</Typography>
                                    </Paper>
                                    
                                    {submission.notes && (
                                      <>
                                        <Typography variant="subtitle1" gutterBottom>Additional Notes</Typography> 
                                        <Paper sx={{ p: 2 }}>
                                          <Typography variant="body2">{submission.notes}</Typography>
                                        </Paper>
                                      </>
                                    )}
                                    
                                    {submission.files && submission.files.length > 0 && (
                                      <>
                                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>File Evidence</Typography>
                                        {renderFileAttachments(submission.files)}
                                      </>
                                    )}
                                    
                                    {submission.adminFeedback && (
                                      <>
                                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Admin Feedback</Typography>
                                        <Paper sx={{ 
                                          p: 2, 
                                          borderLeft: `4px solid ${getStatusColor(submission.status)}` 
                                        }}>
                                          <Typography variant="body2">{submission.adminFeedback}</Typography>
                                          {submission.reviewedAt && (
                                            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                              Provided {formatDistanceToNow(new Date(submission.reviewedAt), { addSuffix: true })}
                                            </Typography>
                                          )}
                                        </Paper>
                                      </>
                                    )}
                                  </Grid>
                                  
                                  {/* Right Column - Status Timeline */}
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle1" gutterBottom>Status Timeline</Typography>
                                    <Paper sx={{ p: 2 }}>
                                      <Stepper activeStep={
                                        submission.status === SUBMISSION_STATUS.SUBMITTED ? 0 :
                                        submission.status === SUBMISSION_STATUS.IN_REVIEW ? 1 :
                                        2
                                      } orientation="vertical">
                                        <Step>
                                          <StepLabel>
                                            <Typography variant="body2">Submitted</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                              {formatDistanceToNow(new Date(submission.submittedAt), { addSuffix: true })}
                                            </Typography>
                                          </StepLabel>
                                        </Step>
                                        <Step>
                                          <StepLabel>
                                            <Typography variant="body2">In Review</Typography>
                                            {submission.reviewStartedAt ? (
                                              <Typography variant="caption" color="textSecondary">
                                                {formatDistanceToNow(new Date(submission.reviewStartedAt), { addSuffix: true })}
                                              </Typography>
                                            ) : (
                                              <Typography variant="caption" color="textSecondary">
                                                Not yet reviewed
                                              </Typography>
                                            )}
                                          </StepLabel>
                                        </Step>
                                        <Step>
                                          <StepLabel error={submission.status === SUBMISSION_STATUS.REJECTED}>
                                            <Typography variant="body2">
                                              {submission.status === SUBMISSION_STATUS.REJECTED ? 
                                                'Rejected' : 'Approved'}
                                            </Typography>
                                            {submission.reviewedAt ? (
                                              <Typography variant="caption" color="textSecondary">
                                                {formatDistanceToNow(new Date(submission.reviewedAt), { addSuffix: true })}
                                              </Typography>
                                            ) : (
                                              <Typography variant="caption" color="textSecondary">
                                                Pending decision
                                              </Typography>
                                            )}
                                          </StepLabel>
                                        </Step>
                                      </Stepper>
                                    </Paper>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      )}
      
      {/* Submission Review Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={handleCloseReviewDialog}
        maxWidth="md"
        fullWidth
      >
        {currentSubmission && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.7), mr: 2 }}>
                  <ActivityTypeIcon type={currentSubmission.activityType} />
                </Avatar>
                <Box>
                  <Typography variant="h6">{currentSubmission.activityName}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Submitted by {currentSubmission.memberName} {formatDistanceToNow(new Date(currentSubmission.submittedAt), { addSuffix: true })}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="subtitle1" gutterBottom>Submission Evidence</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="body1">{currentSubmission.evidence}</Typography>
                  </Paper>
                  
                  {currentSubmission.notes && (
                    <>
                      <Typography variant="subtitle1" gutterBottom>Additional Notes</Typography>
                      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                        <Typography variant="body1">{currentSubmission.notes}</Typography>
                      </Paper>
                    </>
                  )}
                  
                  {currentSubmission.files && currentSubmission.files.length > 0 && (
                    <>
                      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Evidence Files</Typography>
                      {renderFileAttachments(currentSubmission.files)}
                    </>
                  )}
                  
                  <Alert severity="info" icon={<InfoIcon />}>
                    <Typography variant="body2">
                      {currentSubmission.status === SUBMISSION_STATUS.APPROVED || 
                      currentSubmission.status === SUBMISSION_STATUS.REJECTED ? (
                        'This submission has already been reviewed. You can update your feedback and change the decision if needed.'
                      ) : (
                        'Your feedback will be visible to the member. When you approve this submission, tokens will be automatically credited to the member\'s wallet.'
                      )}
                    </Typography>
                  </Alert>
                  
                  <Typography variant="subtitle1" gutterBottom>Admin Feedback</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter feedback for the member (required for approval or rejection)"
                    value={adminFeedback}
                    onChange={(e) => setAdminFeedback(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>Reward Information</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 2 }}>
                      <Chip
                        icon={<TokenIcon />}
                        label={`${currentSubmission.tokenAmount} Tokens`}
                        color="primary"
                        sx={{ fontWeight: 'bold', fontSize: '1.1rem', py: 2 }}
                      />
                    </Box>
                    <Alert severity="warning">
                      <Typography variant="body2">
                        This amount will be automatically credited to the member's wallet upon approval. No additional action will be needed.
                      </Typography>
                    </Alert>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>Submission Status</Typography>
                    <Stepper 
                      activeStep={
                        currentSubmission.status === SUBMISSION_STATUS.SUBMITTED ? 0 :
                        currentSubmission.status === SUBMISSION_STATUS.IN_REVIEW ? 1 :
                        2
                      } 
                      orientation="vertical"
                      sx={{ mt: 2 }}
                    >
                      <Step>
                        <StepLabel>
                          <Typography variant="body2">Submitted</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatDistanceToNow(new Date(currentSubmission.submittedAt), { addSuffix: true })}
                          </Typography>
                        </StepLabel>
                      </Step>
                      <Step>
                        <StepLabel>
                          <Typography variant="body2">In Review</Typography>
                          {currentSubmission.reviewStartedAt ? (
                            <Typography variant="caption" color="textSecondary">
                              {formatDistanceToNow(new Date(currentSubmission.reviewStartedAt), { addSuffix: true })}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              {currentSubmission.status === SUBMISSION_STATUS.SUBMITTED ? 
                                'Not yet reviewed' : 'Started just now'}
                            </Typography>
                          )}
                        </StepLabel>
                      </Step>
                      <Step>
                        <StepLabel error={currentSubmission.status === SUBMISSION_STATUS.REJECTED}>
                          <Typography variant="body2">
                            {currentSubmission.status === SUBMISSION_STATUS.REJECTED ? 
                              'Rejected' : 'Approved'}
                          </Typography>
                          {currentSubmission.reviewedAt ? (
                            <Typography variant="caption" color="textSecondary">
                              {formatDistanceToNow(new Date(currentSubmission.reviewedAt), { addSuffix: true })}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              Pending decision
                            </Typography>
                          )}
                        </StepLabel>
                      </Step>
                    </Stepper>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleCloseReviewDialog} disabled={actionLoading}>
                Cancel
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="outlined"
                color="error"
                startIcon={submissionAction === 'reject' ? <CircularProgress size={20} /> : <CancelIcon />}
                onClick={() => handleUpdateSubmissionStatus('reject')}
                disabled={actionLoading || !adminFeedback.trim()}
                sx={{ mr: 1 }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={submissionAction === 'approve' ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                onClick={() => handleUpdateSubmissionStatus('approve')}
                disabled={actionLoading || !adminFeedback.trim()}
              >
                Approve & Credit Tokens
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Activity Form Dialog */}
      <Dialog
        open={formOpen}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentActivity ? 'Edit Earning Activity' : 'Create New Earning Activity'}
        </DialogTitle>
        <DialogContent dividers>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Activity Name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                fullWidth
                required
                variant="outlined"
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CategoryIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="activityType-label">Activity Type</InputLabel>
                <Select
                  labelId="activityType-label"
                  id="activityType"
                  name="activityType"
                  value={formData.activityType}
                  onChange={(e) => setFormData({...formData, activityType: e.target.value})}
                  label="Activity Type"
                >
                  <MenuItem value="content_creation">Content Creation</MenuItem>
                  <MenuItem value="knowledge_sharing">Knowledge Sharing</MenuItem>
                  <MenuItem value="task_completion">Task Completion</MenuItem>
                  <MenuItem value="community_support">Community Support</MenuItem>
                  <MenuItem value="skill_validation">Skill Validation</MenuItem>
                  <MenuItem value="event_participation">Event Participation</MenuItem>
                  <MenuItem value="referral">Referral</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Token Amount"
                name="tokenAmount"
                type="number"
                value={formData.tokenAmount}
                onChange={(e) => setFormData({...formData, tokenAmount: parseFloat(e.target.value) || 0})}
                fullWidth
                required
                variant="outlined"
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TokenIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Frequency</InputLabel>
                <Select
                  name="frequency"
                  value={formData.frequency}
                  onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                  label="Frequency"
                >
                  <MenuItem value="once">Once per member</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="unlimited">Unlimited</MenuItem>
                </Select>
                <FormHelperText>
                  How often a member can earn tokens from this activity
                </FormHelperText>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max Daily Limit"
                name="maxDaily"
                type="number"
                value={formData.maxDaily}
                onChange={(e) => setFormData({...formData, maxDaily: parseInt(e.target.value) || 0})}
                fullWidth
                variant="outlined"
                margin="normal"
                helperText="Maximum times per day a member can earn from this activity (0 = unlimited)"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TimerIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <FormControlLabel
                  control={
                    <Switch 
                      name="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      color="primary"
                    />
                  }
                  label="Activity is active"
                />
                <FormHelperText>
                  Inactive activities are not shown to members
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseForm} disabled={formLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveActivity}
            disabled={formLoading}
            startIcon={formLoading ? <CircularProgress size={24} /> : null}
          >
            {currentActivity ? 'Update Activity' : 'Create Activity'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the activity "{activityToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
          <Button color="error" onClick={handleDeleteActivity}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EarningActivities;